import 'jspdf-autotable';

import { AppstoreOutlined, DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import { Flex } from 'antd';
import jsPDF from 'jspdf';
import { DateTime } from 'luxon';
import React from 'react';
import { GraphQLTaggedNode } from 'react-relay';

import useAccess from '../hooks/useAccess';
import {
    ActionsType,
    FilterTableParamType,
    OnScheduleItemsImport,
    OnTasksImport,
    TableColumnOptionsType,
    TableColumnSmxType,
} from '../types';
import ControlPanelButton from './ControlPanelButton';
import ControlPanelButtonAdd from './ControlPanelButtonAdd';
import ControlPanelContainer from './ControlPanelContainer';
import ControlPanelDropdown from './ControlPanelDropdown';
import { ControlPanelExportCsvButton } from './controlPanelExportCsvButton';
import { ControlPanelImportCsvButton } from './controlPanelImportCsvButton';
import ControlPanelInput from './ControlPanelInput';
import { actionsForPanel } from './functions';
import useProcessedColumns from './hooks/useProcessedColumns';
import TableGroupFilter from './tableGroupFilter';

interface IControlPanel {
    columns: TableColumnSmxType[];
    actions?: ActionsType[];
    dataRows?: any[];
    editableColumns: TableColumnSmxType[];
    connectionId: string;
    mutationCreate?: GraphQLTaggedNode;
    paramFilter?: FilterTableParamType;
    onScheduleItemsImport?: OnScheduleItemsImport;
    onTasksImport?: OnTasksImport;
}

const ControlPanel: React.FC<IControlPanel> = ({
    columns,
    actions,
    dataRows,
    editableColumns,
    mutationCreate,
    connectionId,
    paramFilter,
    onScheduleItemsImport,
    onTasksImport,
}) => {
    const actionsPanel = actions ? actionsForPanel(actions) : [];
    const options = useProcessedColumns<TableColumnOptionsType>(columns, 'options');

    const tableGroupFilter = actionsPanel.includes('filter') ? (
        <TableGroupFilter paramFilter={paramFilter} />
    ) : (
        <div></div>
    );

    const tableFilterAndSearch = actionsPanel.includes('search') ? (
        <Flex gap={10} align={'center'}>
            <ControlPanelInput icon={<SearchOutlined />} title="Search" />
        </Flex>
    ) : (
        tableGroupFilter
    );

    const isShowPanel =
        actionsPanel.includes('columnsView') || actionsPanel.includes('export') || actionsPanel.includes('createNew');

    const exportToPDF = () => {
        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'a3',
        });
        doc.setFontSize(20);
        // doc.text('My table', 14, 15);

        const headers = options.map((column) => column.label);
        const rows = dataRows?.map((row) => {
            return options.map((col) => {
                return row[col.key];
            });
        });

        (doc as any).autoTable({
            head: [headers],
            body: rows,
            startY: 10,
            theme: 'grid',
            headStyles: { fillColor: [100, 100, 255] },
            columnStyles: {
                0: { cellWidth: 'auto' },
                1: { cellWidth: 'auto' },
            },
        });

        doc.save(`Export_${DateTime.now().toFormat('yyyy_mm_dd_hh:mm')}.pdf`);
    };

    const accessToImportButton = useAccess([
        'schedule-task.all.create',
        'schedule-task.all.edit',
        'schedule-task.all.manage',
    ]);

    return (
        <ControlPanelContainer>
            <Flex gap={10} justify={'space-between'} align={'center'}>
                {tableFilterAndSearch}
                {isShowPanel && (
                    <Flex gap={10} align={'center'}>
                        {actionsPanel.includes('import-csv') && accessToImportButton && (
                            <ControlPanelImportCsvButton
                                onScheduleItemsImport={onScheduleItemsImport}
                                onTasksImport={onTasksImport}
                            />
                        )}
                        {actionsPanel.includes('columnsView') && (
                            <ControlPanelDropdown icon={<AppstoreOutlined />} title="Columns" columns={columns} />
                        )}
                        {actionsPanel.includes('export') && (
                            <ControlPanelButton icon={<DownloadOutlined />} title="Export" onClick={exportToPDF} />
                        )}

                        {actionsPanel.includes('export-csv') && <ControlPanelExportCsvButton />}

                        {actionsPanel.includes('createNew') && mutationCreate && (
                            <ControlPanelButtonAdd
                                editableColumns={editableColumns}
                                connectionId={connectionId}
                                mutationCreate={mutationCreate}
                            />
                        )}
                    </Flex>
                )}
            </Flex>
            {actionsPanel.includes('filter') && actionsPanel.includes('search') && (
                <TableGroupFilter paramFilter={paramFilter} />
            )}
        </ControlPanelContainer>
    );
};

export default ControlPanel;
